import React from 'react';
import _ from 'lodash';
import { useForm } from 'react-hook-form';
import { Divider, styled } from '@mui/material';
import { computeOverlaps } from './helpers/helpers';
import type { FlexWorker, ShiftApplication } from './bulk-accept-shift-dialog.types';
import { BulkAcceptShiftItem } from './bulk-accept-shift-item';

const BulkAcceptShiftDialogForm = ({
  flexWorker,
  onSubmit,
  shiftApplications,
  ...props
}: Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  flexWorker: FlexWorker;
  onSubmit: (shiftApplicationIds: string[]) => void;
  shiftApplications: { current: ShiftApplication; rest: ShiftApplication[] };
}) => {
  const { control, watch, handleSubmit } = useForm<Record<string, boolean>>({
    defaultValues: getDefaultCheckboxValues(shiftApplications.current, shiftApplications.rest),
  });
  const checkboxes = watch();
  const overlaps = computeOverlaps([...shiftApplications.rest, shiftApplications.current]);
  const [disabledShiftApplicationIds, setDisabledShiftApplicationIds] = React.useState<string[]>(
    []
  );

  React.useEffect(() => {
    const [selectedShiftApplicationIds, notSelectedShiftApplicationIds] = _(checkboxes)
      .keys()
      .partition((key) => checkboxes[key])
      .value();

    const disabled = notSelectedShiftApplicationIds
      .map((shiftApplicationId) => {
        const overlappingShiftApplicationIds = overlaps[shiftApplicationId];

        return _.intersection(selectedShiftApplicationIds, overlappingShiftApplicationIds).length >
          0
          ? shiftApplicationId
          : undefined;
      })
      .filter(_.isString);

    setDisabledShiftApplicationIds((cur) => {
      if (_.isEqual(cur.sort(), disabled.sort())) {
        return cur;
      }

      return disabled;
    });
  }, [checkboxes, shiftApplications, overlaps]);

  const isThereOverlap = disabledShiftApplicationIds.length > 0;

  return (
    <>
      <p data-testid="og-client-callout">
        Wat fijn! {flexWorker.firstName} wil meer shifts bij je komen werken. Geef hieronder aan
        wanneer je zijn/haar helpende hand nog kan gebruiken.
      </p>
      <BulkAcceptShiftItem
        name={shiftApplications.current.id}
        shiftInterval={shiftApplications.current.shift.interval}
        hourlyRateCents={shiftApplications.current.hourlyRateCents}
        hasDivergentRate={shiftApplications.current.hasDivergentRate}
        control={control}
        disabled
      />
      <StyledDivider />
      <form
        style={{
          maxHeight: '13.5rem',
          overflow: 'auto',
        }}
        {...props}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(() => onSubmit(Object.keys(checkboxes).filter((id) => checkboxes[id])))(e);
        }}
      >
        {shiftApplications.rest.map(({ id, shift, hourlyRateCents, hasDivergentRate }) => (
          <BulkAcceptShiftItem
            name={id}
            key={id}
            control={control}
            disabled={disabledShiftApplicationIds.includes(id)}
            shiftInterval={shift.interval}
            hourlyRateCents={hourlyRateCents}
            hasDivergentRate={hasDivergentRate}
          />
        ))}
      </form>
      {isThereOverlap ? (
        <p data-testid="shifts-overlap-warning">
          Let op! {flexWorker.firstName} is ijverig, maar kan geen dubbele diensten draaien op
          hetzelfde moment. Kijk en selecteer welke shift(s) wél handig is/zijn.
        </p>
      ) : null}
    </>
  );
};

const getDefaultCheckboxValues = (
  currentShiftApplication: ShiftApplication,
  otherShiftApplications: ShiftApplication[]
) => {
  const defaultValues = otherShiftApplications.reduce(
    (acc, cur) => ({ ...acc, [cur.id]: false }),
    {}
  );

  return {
    ...defaultValues,
    [currentShiftApplication.id]: true,
  };
};

const StyledDivider = styled(Divider)(({ theme }) => ({
  background: theme.palette.grey[300],
  border: 0,
  height: '1px',
}));

export { BulkAcceptShiftDialogForm };
